import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Activity, Users, Search, Eye, CheckCircle, Clock, AlertCircle } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach } from '../../lib/auth';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { CoachProgressView } from '../../components/coach/CoachProgressView';

interface RespondentSummary {
  id: string;
  userId: string;
  name: string;
  email: string;
  hasCompletedAssessment: boolean;
}

export function ClientProgress() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [respondents, setRespondents] = useState<RespondentSummary[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [coachId, setCoachId] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) {
          navigate('/');
          return;
        }

        const { data: coach } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) {
          navigate('/login');
          return;
        }

        setCoachId(coach.id);

        const { data: respondentsData } = await supabase
          .from('respondents')
          .select('id, user_id, users!respondents_user_id_fkey(full_name, email)')
          .eq('coach_id', coach.id);

        const userIds = (respondentsData || []).map(r => r.user_id);
        const completedSet = new Set<string>();

        if (userIds.length > 0) {
          const { data: results } = await supabase
            .from('results')
            .select('user_id')
            .in('user_id', userIds);
          (results || []).forEach(r => completedSet.add(r.user_id));
        }

        const mapped = (respondentsData || []).map(r => ({
          id: r.id,
          userId: r.user_id,
          name: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
          email: (r.users as any)?.email || '',
          hasCompletedAssessment: completedSet.has(r.user_id),
        }));

        setRespondents(mapped);
        if (mapped.length > 0) setSelectedUserId(mapped[0].userId);
      } catch (err) {
        console.error('Error loading respondents:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const filtered = respondents.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedRespondent = respondents.find(r => r.userId === selectedUserId);

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading progress..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-sage-500 rounded-xl shadow-soft">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">Client Progress</h1>
              <p className="freud-text-sm text-chocolate-500">Track goals, habits, tasks and check-ins across your clients</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {respondents.length === 0 ? (
          <div className="freud-card-elevated p-12 text-center">
            <Users className="w-12 h-12 text-cream-400 mx-auto mb-4" />
            <h2 className="freud-heading-xl text-chocolate-900 mb-2">No Respondents Yet</h2>
            <p className="text-chocolate-500 mb-6">Add respondents to start tracking their progress.</p>
            <Link to="/coach/respondents" className="freud-btn-primary inline-flex items-center gap-2">
              <Users className="w-4 h-4" />
              Go to Respondents
            </Link>
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Respondent list panel */}
            <div className="w-72 flex-shrink-0">
              <div className="freud-card-elevated overflow-hidden">
                <div className="p-4 border-b border-cream-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-chocolate-400" />
                    <input
                      type="text"
                      placeholder="Search clients..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="freud-input pl-9 py-2 text-sm"
                    />
                  </div>
                </div>
                <div className="divide-y divide-cream-100 max-h-[calc(100vh-280px)] overflow-y-auto">
                  {filtered.length === 0 ? (
                    <div className="py-8 text-center text-chocolate-400">
                      <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No clients found</p>
                    </div>
                  ) : (
                    filtered.map(r => (
                      <button
                        key={r.id}
                        onClick={() => setSelectedUserId(r.userId)}
                        className={`w-full text-left px-4 py-3 transition-colors hover:bg-cream-50 ${
                          selectedUserId === r.userId ? 'bg-sage-50 border-l-2 border-sage-500' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-chocolate-900 truncate">{r.name}</p>
                            <p className="text-xs text-chocolate-400 truncate">{r.email}</p>
                          </div>
                          {r.hasCompletedAssessment ? (
                            <CheckCircle className="w-4 h-4 text-sage-500 flex-shrink-0" />
                          ) : (
                            <Clock className="w-4 h-4 text-warm-400 flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Progress detail panel */}
            <div className="flex-1 min-w-0">
              {selectedUserId ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="freud-heading-xl text-chocolate-900">{selectedRespondent?.name}</h2>
                      <p className="text-sm text-chocolate-500">{selectedRespondent?.email}</p>
                    </div>
                    {selectedRespondent?.hasCompletedAssessment && (
                      <Link
                        to={`/coach/results/${selectedUserId}`}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-sage-200 bg-sage-50 text-sage-700 hover:bg-sage-100 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View Results
                      </Link>
                    )}
                  </div>
                  <CoachProgressView userId={selectedUserId} coachId={coachId || undefined} />
                </>
              ) : (
                <div className="freud-card-elevated p-12 text-center">
                  <Activity className="w-12 h-12 text-cream-400 mx-auto mb-3" />
                  <p className="text-chocolate-500">Select a client to view their progress</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
